// Walkthrough — 4-screen first-visit tour for the parent-app.
//
// Mounted by TodayWidget when localStorage has no "seen" flag. On
// dismissal (Done, Skip, ESC, or the push-CTA jump) we mark the flag
// so the tour never auto-shows again. A discreet "Take the tour
// again" link in Settings resets the flag for parents who want it.
//
// State machine (closed):
//
//   step 0 (welcome)
//     → step 1 (today)
//     → step 2 (inbox)
//     → step 3 (push)  → onEnablePush (jumps to Settings) OR Done
//
// Keyboard: ArrowRight / Enter → next; ArrowLeft → back; ESC → skip.
// Focus management: on mount we focus the Next button so keyboard
// users can drive immediately. Focus stays inside the dialog via a
// small loop in onKeyDown; we don't use a full focus-trap library
// because the dialog has only 2-3 focusable controls.
//
// Visual: centered card on a soft slate backdrop; brand mark at the
// top, large icon in the middle (120×120 line SVG), title + one
// supporting sentence, pip indicator + Next/Back/Skip controls at
// the bottom. Mirrors PageShell's card+brand+footer layout pattern.

import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button, Card, Stack, tokens } from "@sms/design-system";
import type { Locale } from "@sms/i18n";

import { makeT } from "../../i18n";
import { markWalkthroughSeen } from "./walkthrough-state";

export interface WalkthroughProps {
  locale?: Locale;
  /** Called when the parent dismisses the walkthrough by any means
   *  (Done, Skip, ESC). The walkthrough has already marked the flag
   *  before this fires. */
  onDone: () => void;
  /** Called when the parent taps the step-4 "Enable push" CTA. Same
   *  contract as onDone PLUS the host should switch to Settings so
   *  the PushToggleSection is visible. If unwired, the CTA degrades
   *  to a plain Done. */
  onEnablePush?: () => void;
}

interface StepDef {
  icon: string; // /empty/<name>.svg
  titleKey: string;
  bodyKey: string;
}

const STEPS: StepDef[] = [
  { icon: "/empty/welcome.svg", titleKey: "walkthrough.welcome_title", bodyKey: "walkthrough.welcome_body" },
  { icon: "/empty/today.svg",   titleKey: "walkthrough.today_title",   bodyKey: "walkthrough.today_body" },
  { icon: "/empty/inbox.svg",   titleKey: "walkthrough.inbox_title",   bodyKey: "walkthrough.inbox_body" },
  { icon: "/empty/push.svg",    titleKey: "walkthrough.push_title",    bodyKey: "walkthrough.push_body" },
];

export function Walkthrough({ locale = "ne", onDone, onEnablePush }: WalkthroughProps) {
  const t = makeT(locale);
  const [step, setStep] = useState(0);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Focus the primary action button on each step so keyboard users
  // can drive the flow with just Enter/ArrowRight. Button is a
  // design-system primitive that doesn't forward ref, so we locate
  // it via the testid that's already on the element.
  useEffect(() => {
    const btn = dialogRef.current?.querySelector<HTMLButtonElement>(
      '[data-testid="walkthrough-next"], [data-testid="walkthrough-finish"]',
    );
    btn?.focus();
  }, [step]);

  const finish = (action: "done" | "push") => {
    markWalkthroughSeen();
    if (action === "push" && onEnablePush) {
      onEnablePush();
    } else {
      onDone();
    }
  };

  const next = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      // Step 4 — the push CTA. If we don't have a push handler wired
      // (degraded mode), still close cleanly.
      finish(onEnablePush ? "push" : "done");
    }
  };
  const back = () => {
    if (step > 0) setStep(step - 1);
  };
  const skip = () => finish("done");

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      skip();
      return;
    }
    if (e.key === "Enter" || e.key === "ArrowRight") {
      e.preventDefault();
      next();
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      back();
    }
  };

  const current = STEPS[step]!;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      style={backdropStyle}
      data-testid="walkthrough"
      data-step={step}
      role="dialog"
      aria-modal="true"
      aria-labelledby="walkthrough-title"
      onKeyDown={onKeyDown}
      ref={dialogRef}
    >
      <Card variant="elevated" padding={6} style={cardStyle}>
        <Stack gap={5} align="center">
          {/* Skip button, top-right. Always visible so parents who
              don't want the tour can leave at any step. */}
          <Stack
            direction="row"
            justify="flex-end"
            style={{ width: "100%" }}
          >
            <button
              type="button"
              onClick={skip}
              data-testid="walkthrough-skip"
              style={skipButtonStyle}
              aria-label={t("walkthrough.skip")}
            >
              {t("walkthrough.skip")}
            </button>
          </Stack>

          <Hero src={current.icon} alt="" />

          <Stack gap={2} align="center">
            <h1 id="walkthrough-title" style={titleStyle}>
              {t(current.titleKey)}
            </h1>
            <p style={bodyStyle}>{t(current.bodyKey)}</p>
          </Stack>

          <PipIndicator count={STEPS.length} active={step} />

          <Stack
            direction="row"
            gap={2}
            justify="space-between"
            style={{ width: "100%" }}
          >
            {step > 0 ? (
              <Button
                variant="secondary"
                onClick={back}
                data-testid="walkthrough-back"
              >
                {t("walkthrough.back")}
              </Button>
            ) : (
              <span />
            )}
            <Button
              onClick={next}
              data-testid={isLast ? "walkthrough-finish" : "walkthrough-next"}
            >
              {isLast
                ? onEnablePush
                  ? t("walkthrough.enable_push")
                  : t("walkthrough.done")
                : t("walkthrough.next")}
            </Button>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
}

function Hero({ src, alt }: { src: string; alt: string }) {
  return (
    <div style={heroWrapStyle}>
      <img src={src} alt={alt} width={120} height={120} style={{ display: "block" }} />
    </div>
  );
}

function PipIndicator({ count, active }: { count: number; active: number }) {
  return (
    <div style={pipRowStyle} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          style={i === active ? pipActiveStyle : pipDotStyle}
          data-active={i === active ? "true" : "false"}
        />
      ))}
    </div>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: tokens.space[4],
  background: "rgba(15, 23, 42, 0.6)", // slate-900 / 60%
};
const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 440,
};
const heroWrapStyle: React.CSSProperties = {
  width: 144,
  height: 144,
  borderRadius: tokens.radius.xl,
  background: tokens.color.brand[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: tokens.type.weight.bold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.02em",
  margin: 0,
  textAlign: "center",
};
const bodyStyle: React.CSSProperties = {
  fontSize: tokens.type.size.base,
  color: tokens.color.semantic.textMuted,
  margin: 0,
  textAlign: "center",
  maxWidth: "32ch",
  lineHeight: 1.5,
};
const pipRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
};
const pipDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: tokens.color.semantic.border,
  transition: "background 160ms ease, width 160ms ease",
};
const pipActiveStyle: React.CSSProperties = {
  ...pipDotStyle,
  background: tokens.color.brand[500],
  width: 20,
  borderRadius: 4,
};
const skipButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: tokens.color.semantic.textMuted,
  fontSize: tokens.type.size.sm,
  fontWeight: tokens.type.weight.medium,
  cursor: "pointer",
  padding: 4,
};

// Avoid unused-import lint trip — ReactNode is referenced only
// indirectly via the Stack/Card children types.
const _unusedReactNode: ReactNode = null;
void _unusedReactNode;
